<!-- app-subdomain-creators/src/Components/CreatorChatConversationStatus.vue -->

<template>
  <div class="flex items-center gap-2">
    <!-- CLOSE / REOPEN -->
    <div class="relative">
      <button
        type="button"
        class="w-9 h-9 rounded-full bg-white flex items-center justify-center
               hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed"
        :disabled="isDisabled || isSaving"
        @click="toggleClosed"
        @mouseenter="showCloseTooltip = true"
        @mouseleave="showCloseTooltip = false"
      >
        <CheckIcon
          class="w-6 h-6"
          :class="isClosed ? 'text-green-600' : 'text-zinc-300'"
        />
      </button>

      <!-- Close / Reopen tooltip (same style as Search Chat) -->
      <div
        v-if="showCloseTooltip"
        class="absolute text-center w-28 bottom-full p-2 -translate-x-[122px] -mb-14
               bg-black opacity-60 text-white text-sm rounded tooltip"
      >
        {{ closeTooltipText }}
        <div
          class="tooltip-arrow absolute top-1/2 -right-1 mt-[-5px] w-0 h-0
                 border-l-[12px] border-transparent border-b-[12px]
                 transform -translate-y-1/2 rotate-45 rounded bg-black"
        ></div>
      </div>
    </div>

    <!-- SAVE / UNSAVE -->
    <div class="relative">
      <button
        type="button"
        class="w-9 h-9 rounded-full bg-white flex items-center justify-center
               hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed"
        :disabled="isDisabled || isSaving"
        @click="toggleSaved"
        @mouseenter="showSavedTooltip = true"
        @mouseleave="showSavedTooltip = false"
      >
        <StarIcon
          class="w-6 h-6"
          :class="isSaved ? 'text-yellow-400' : 'text-zinc-300'"
        />
      </button>

      <!-- Save / Unsave tooltip (same style as Search Chat) -->
      <div
        v-if="showSavedTooltip"
        class="absolute text-center w-28 bottom-full p-2 -translate-x-[122px] -mb-14
               bg-black opacity-60 text-white text-sm rounded tooltip"
      >
        {{ saveTooltipText }}
        <div
          class="tooltip-arrow absolute top-1/2 -right-1 mt-[-5px] w-0 h-0
                 border-l-[12px] border-transparent border-b-[12px]
                 transform -translate-y-1/2 rotate-45 rounded bg-black"
        ></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { CheckIcon, StarIcon } from '@heroicons/vue/24/solid'

const props = defineProps({
  member: { type: Object, default: null },
  featureInstanceId: { type: [Number, String], default: null },
})

const emit = defineEmits(['update-member'])

const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL
const isSaving = ref(false)

// Tooltip visibility flags
const showCloseTooltip = ref(false)
const showSavedTooltip = ref(false)

const isDisabled = computed(() => {
  return !props.member || !props.featureInstanceId
})

const isClosed = computed(() => {
  const s = String(props.member?.status || 'Open')
  return s === 'Closed'
})

const isSaved = computed(() => {
  // true if explicit flag OR if the conversation_status we get is "Saved"
  return props.member?.isSaved === true || props.member?.status === 'Saved'
})

// Tooltip text (and HTML title) driven by state
const closeTooltipText = computed(() =>
  isClosed.value ? 'Reopen conversation' : 'Close conversation'
)

const saveTooltipText = computed(() =>
  isSaved.value ? 'Unsave conversation' : 'Save conversation'
)

async function postConversationStateUpdate(patch) {
  const payload = {
    featureInstanceId: Number(props.featureInstanceId),
    memberId: Number(props.member?.id),
    ...patch,
  }

  const resp = await fetch(`${apiProxyUrl}/api/chat-conversation-state`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })

  if (!resp.ok) {
    console.warn('[ChatConversationStatus] Persist failed:', resp.status)
    return false
  }

  return true
}

async function markConversationRead() {
  const resp = await fetch(`${apiProxyUrl}/api/chat-mark-read`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      featureInstanceId: Number(props.featureInstanceId),
      memberId: Number(props.member?.id),
    }),
  })

  if (!resp.ok) {
    console.warn('[ChatConversationStatus] mark-read failed:', resp.status)
    return false
  }
  return true
}

function emitPatch(patch) {
  emit('update-member', {
    memberId: Number(props.member?.id),
    featureInstanceId: Number(props.featureInstanceId),
    patch,
  })
}

async function toggleClosed() {
  if (isDisabled.value || isSaving.value) return
  isSaving.value = true

  try {
    if (!props.member) return

    // Reopen
    if (isClosed.value) {
      emitPatch({ status: 'Open' })
      await postConversationStateUpdate({ status: 'Open' })
      return
    }

    // Close: mark read then close + clear unread locally (via parent)
    await markConversationRead()

    emitPatch({ unreadCount: 0, status: 'Closed' })
    await postConversationStateUpdate({ status: 'Closed', unreadCount: 0 })
  } finally {
    isSaving.value = false
  }
}

async function toggleSaved() {
  if (isDisabled.value || isSaving.value) return
  isSaving.value = true

  try {
    if (!props.member) return

    const nextIsSaved = !isSaved.value
    const nextStatus = nextIsSaved ? 'Saved' : 'Open'

    // local optimistic update
    emitPatch({
      isSaved: nextIsSaved,
      status: nextStatus,
    })

    await postConversationStateUpdate({
      status: nextStatus,
    })
  } finally {
    isSaving.value = false
  }
}
</script>


